<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInflationIndexedPriceChangeNotificationCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    notification: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const headerText = ref($fn.value.gettext('Inflation indexed price change'));
  const ux = ref([]);

  const $data = computed(() => ({
    headerText: headerText.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'headerText' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { headerText, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.notification,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('notification');
    }
  );

  const act_7751968922212722_showModal_0ftn9c6xn_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InflationIndexedPriceChange',
      props: {
        id: props.notification.id,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_7rwha1rtk =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_r2vm3yeiy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7rwha1rtk,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_r2vm3yeiy],
    event: 'reload',
  });
  const act_7751968922212722_reloadComponentData_ldhe61kpv =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7751968922212722_close_dvqzhlfr1 =
    new actionTypes.EventHandlerImpl({
      action: act_7751968922212722_reloadComponentData_ldhe61kpv,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_7751968922212722_showModal_0ftn9c6xn =
    new actionTypes.ShowModalAction({
      actionArgs: act_7751968922212722_showModal_0ftn9c6xn_getActionArgs,
      events: [inst_evh_7751968922212722_close_dvqzhlfr1],
    });
  const inst_evh_7751968922212722_click_kliermw7i =
    new actionTypes.EventHandlerImpl({
      action: act_7751968922212722_showModal_0ftn9c6xn,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7751968922212722_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7751968922212722_click_kliermw7i],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_r2vm3yeiy,
    inst_evh_7315092382398562_reload,
    inst_evh_7751968922212722_close_dvqzhlfr1,
    inst_evh_7751968922212722_click_kliermw7i,
    inst_evh_7751968922212722_click,
  };

  const evh_7315092382398562_reload_r2vm3yeiy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_r2vm3yeiy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7751968922212722_close_dvqzhlfr1 = async (event, scope) => {
    return await actions.inst_evh_7751968922212722_close_dvqzhlfr1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7751968922212722_click_kliermw7i = async (event, scope) => {
    return await actions.inst_evh_7751968922212722_click_kliermw7i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7751968922212722_click = async (event, scope) => {
    return await actions.inst_evh_7751968922212722_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox direction="row" alignItems="start">
      <div class="me-3 pt-1">
        <Icon icon="ant-design:percentage-outlined" :tooltip="headerText">
        </Icon>
      </div>
      <ItpBox direction="column">
        <ItpText :text="headerText" strong tag="h5"> </ItpText>
        <div class="pb-2">
          <strong class="me-2">
            {{ notification.data.service_data.name }}
          </strong>
          <small class="me-2">
            {{ gettext('current price') }}
          </small>
          <strong class="me-2">
            {{ number(notification.data.source_object.amount) }}
            {{ notification.data.source_object.currency }}
          </strong>
          <small class="me-2">
            {{ gettext('new price') }}
          </small>
          <strong>
            {{ number(notification.data.new_price) }}
            {{ notification.data.source_object.currency }}
          </strong>
          <ItpButton
            :text="gettext('Add new price...')"
            variant="light"
            size="sm"
            class="ms-3"
            @click="evh_7751968922212722_click($event, {})"
          >
          </ItpButton>
        </div>
        <ItpBox direction="row" class="mb-2">
          <UcContractMiniCard :item="notification.data.contract_data" no-icon>
          </UcContractMiniCard>
          <span class="mx-3"> &middot; </span>
          <UcCompanyMiniCard
            :item="notification.data.contract_data.counterparty"
            no-icon
          >
          </UcCompanyMiniCard>
        </ItpBox>
        <ItpText small muted>
          {{ datetime(notification.created_at) }}
          {{ username(notification.created_by) }}
        </ItpText>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
